/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, ChartEvent, ActiveElement, Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import CloseIcon from '@material-ui/icons/Close';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative'
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        }
    }),
);

interface TrtustFundsAvailableChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter; 
}

interface ChartState {
    debtors: number[];
    trustAmount: number[];
    fundsAvailable: number[];
    grouping: string[];
    format: string[];
    totalDebtors: number;
    totalTrustAmount: number;
    totalFundsAvailable: number;
    symbol: string;
    homeCountry: string;
}

export const TrustFundsAvaialbleChart: React.FC<TrtustFundsAvailableChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TrustFundsAvaialbleData, TrustFundsAvailableParams>(TRUST_FUNDS_AVAILABLE_QUERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        debtors: [],
        trustAmount: [],
        fundsAvailable: [],
        grouping: [],
        format: [],
        totalDebtors: 0,
        totalTrustAmount: 0,
        totalFundsAvailable: 0,
        symbol: '$',
        homeCountry: 'AU'
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);
    const countryQuery = useQuery<CountryData>(COUNTRY_QUERY);

    useEffect(() => {
        if (
            countryQuery.data &&
            countryQuery.data.settings &&
            countryQuery.data.settings.systems &&
            countryQuery.data.settings.systems.regionSettings &&
            countryQuery.data.settings.systems.regionSettings.config
        ) {
            const { homeCountry } = countryQuery.data.settings.systems.regionSettings.config;

            setState((prevState) => {
                return {
                    ...prevState,
                    homeCountry,
                };
            });
        }
    // tslint:disable-next-line: align
    }, [countryQuery.data, countryQuery.loading, countryQuery.error]);

    const getChartTitle = (homeCountry: string) => {
        if (homeCountry && homeCountry.toLowerCase() === 'au') {
            return 'Potential Trust Account to Office Transfers';
        } else {
            return 'Potential Client Account to Office Transfers';
        }
    };

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line:no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isTrustFundsAvailableChartOpen`);
        }
    };

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let debtors: number[] = [];
                let trustAmount: number[] = [];
                let fundsAvailable: number[] = [];
                let grouping: string[] = [];
                let format: string[] = [];
                let totalDebtors: number = 0;
                let totalTrustAmount: number = 0;
                let totalFundsAvailable: number = 0;

                if (!error && data && data.statistics && data.statistics.matter) {
                    let availableData = data.statistics.matter.trustFundsAvailable;

                    debtors = availableData.trustFundsAvailableByClient.map((o) => { return o.debtors; });
                    trustAmount = availableData.trustFundsAvailableByClient.map((o) => { return o.trustAmount; });
                    fundsAvailable = availableData.trustFundsAvailableByClient.map((o) => { return o.fundsAvailable; });
                    grouping = availableData.trustFundsAvailableByClient.map((o) => { return o.grouping; });
                    format = availableData.trustFundsAvailableByClient.map((o) => { return o.format; });

                    totalDebtors = availableData.summary.totalDebtors;
                    totalTrustAmount = availableData.summary.totalTrustAmount;
                    totalFundsAvailable = availableData.summary.totalFundsAvailable;
                }

                return {
                    ...prevState,
                    debtors,
                    trustAmount,
                    fundsAvailable,
                    grouping,
                    format,
                    totalDebtors,
                    totalTrustAmount,
                    totalFundsAvailable
                };
            });
        }

        // tslint:disable-next-line: align
    }, [loading, error, data]);

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            searchCriterias.push(
                { property: 'Debtors', operator: 'gt', comparison: '0' },
                { property: 'TrustBalance', operator: 'gt', comparison: '0' },
            );

            props.getSelectedItem(searchCriterias, true, getChartTitle(state.homeCountry), DrillDownType.matterSummaryDrilldown, 'Trust/Client Funds');
        }
    };

    if (loading || currencySymbolQuery.loading || countryQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }
    
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.debtors, state.trustAmount) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<InsertChartOutlinedIcon />} title={getChartTitle(state.homeCountry)} />
                <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                    <AnimatedNumber value={state.totalFundsAvailable} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                </div>
                <div className={classes.title}>Balance by Client</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.grouping[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            searchCriterias.push(
                                { property: 'Client', operator: 'eq', comparison: item },
                                { property: 'Debtors', operator: 'gt', comparison: '0' },
                                { property: 'TrustBalance', operator: 'gt', comparison: '0' },
                            );

                            props.getSelectedItem(
                                searchCriterias, 
                                true, 
                                `${getChartTitle(state.homeCountry)} for ${item}`,
                                DrillDownType.matterSummaryDrilldown, 
                                'Matter'
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.format),
                        datasets: [
                            {
                                label: 'Debtors',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.debtors,
                            },
                            {
                                label: 'Trust Amount',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft),
                                data: state.trustAmount,
                            }
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.trustAmount, ...state.debtors]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.trustAmount, ...state.debtors]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            formattedValue,
                                            dataset
                                        } = context;
                                        var currentValue = convertStringNumberToNumber(formattedValue);
                                        // return `${dataset.label }: ${state.symbol}${formatNumber(currentValue)}`;
                                        return [
                                            `${dataset.label }: ${state.symbol}${formatNumber(currentValue)}`,
                                            `Potential Transfer: ${state.symbol}${formatNumber(state.fundsAvailable[context.dataIndex])}`
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.debtors, state.trustAmount) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface TrustFundsAvailableParams {
    entityType?: number;
    entityGuid?: string;
    responsibility?: number;
}

export interface TrustFundsAvaialbleData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    trustFundsAvailable: TrustFundsAvaialbleGrouped;
}

export interface TrustFundsAvaialbleGrouped {
    trustFundsAvailableByClient: TrustFundsAvailable[];
    summary: TrustFundsAvailableSummary;
}

export interface TrustFundsAvailableSummary {
    totalDebtors: number;
    totalTrustAmount: number;
    totalFundsAvailable: number;
}

export interface TrustFundsAvailable {
    debtors: number;
    trustAmount: number;
    fundsAvailable: number;
    grouping: string;
    format: string;
}

const TRUST_FUNDS_AVAILABLE_QUERY = gql`
query TrustFundsAvailableGrouped($entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
      matter {
        trustFundsAvailable (entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
          trustFundsAvailableByClient {
            debtors
            trustAmount
            fundsAvailable
            grouping
            format
          }
          summary {
            totalTrustAmount
            totalDebtors
            totalFundsAvailable
          }
        }
      }
    }
  }
`;

export interface CountryData {
    settings: Settings;
}

export interface Settings {
    systems: Systems;
}

export interface Systems {
    regionSettings: RegionSettings;
}

export interface RegionSettings {
    config: Config;
}

export interface Config {
    homeCountry: string;
}

const COUNTRY_QUERY = gql`
    query countryQuery {
        settings {
            systems {
                regionSettings {
                    config {
                        homeCountry
                    }
                }
            }
        }
    }
`;